import { nameof } from "bindable-data";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { gsap } from "gsap";
import { Container, IDestroyOptions } from "pixi.js";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { SceneIndex } from "../../../SceneIndex";
import { AnimationTargetProvider } from "../../../common/components/AnimationTargetProvider";
import { IFirstTimeExperienceModel } from "../../../common/models/IFirstTimeExperienceModel";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { IGameController } from "../../controllers/IGameController";
import { NewsCard } from "../../flow/impl/news/NewsCard";
import { RaidNewsCard } from "../../flow/impl/news/RaidNewsCard";
import { VisitNewsCard } from "../../flow/impl/news/VisitNewsCard";
import { INewsModel } from "../../models/INewsModel";
import { NewsResourcePackage } from "../../resources/NewsResourcePackage";
import { FullscreenInputBlocker } from "../FullscreenInputBlocker";
import { NewsCardView } from "./cards/NewsCardView";
import { RaidNewsCardView } from "./cards/RaidNewsCardView";
import { RaidNewsCardViewWithoutShields } from "./cards/RaidNewsCardViewWithoutShields";
import { VisitNewsCardView } from "./cards/VisitNewsCardView";
import { StealNewsCard } from "../../flow/impl/news/StealNewsCard";
import { StealNewsCardView } from "./cards/StealNewsCardView";
import { UserProfileResourcePackage } from "../../../popups/userprofile/resources/UserProfileResourcePackage";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";

export class NewsPresenter extends Container {
    private readonly newsModel: INewsModel;
    private readonly newsResourcesPackage: NewsResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly canvasScaler: ICanvasScaler;
    private readonly playerModel: IPlayerModel;
    private readonly newsAnimationTargetProvider: AnimationTargetProvider;
    private readonly gameController: IGameController;
    private readonly inputBlocker: FullscreenInputBlocker;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private accrueResultTimeOffset: number;

    public constructor(
        newsModel: INewsModel,
        playerModel: IPlayerModel,
        newsResourcesPackage: NewsResourcePackage,
        commonResources: CommonResourcesPackage,
        userProfileResources: UserProfileResourcePackage,
        canvasScaler: ICanvasScaler,
        newsAnimationTargetProvider: AnimationTargetProvider,
        gameController: IGameController,
        firstTimeExperienceModel: IFirstTimeExperienceModel,
        asyncOperationNotification: AsyncOperationNotification
    ) {
        super();
        this.newsModel = newsModel;
        this.playerModel = playerModel;
        this.newsResourcesPackage = newsResourcesPackage;
        this.commonResources = commonResources;
        this.userProfileResources = userProfileResources;
        this.canvasScaler = canvasScaler;
        this.newsAnimationTargetProvider = newsAnimationTargetProvider;
        this.gameController = gameController;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.inputBlocker.blocked = false;
        this.addChild(this.inputBlocker);
        this.newsModel.propertyChanged.add(this.onNewsModelChange, this);
        this.onNewsModelChange(this.newsModel, nameof(this.newsModel, "news"));
    }

    private async onNewsModelChange(model: INewsModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "news"):
                const news = this.newsModel.news.value;
                if (news == null) {
                    return;
                }
                this.inputBlocker.blocked = true;
                if (!news.afterClaim) {
                    await this.newsModel.claimAction();
                    return;
                }
                try {
                    const buildingWillBeDoungraded = this.newsModel.news.value.buildingWillBeDoungraded?.length > 0;
                    if (buildingWillBeDoungraded) {
                        this.playerModel.loadBuildings();
                        this.playerModel.loadPlayerData();
                    }
                    if (buildingWillBeDoungraded && this.firstTimeExperienceModel.tooltipsInfo.value.fixIntro) {
                        this.gameController.showPopupBySceneIndex(SceneIndex.CHECKOUT_FIX_UPGRADE);
                    }
                    await this.show(news);
                    await this.newsModel.markRead();
                } finally {
                    this.inputBlocker.blocked = false;
                }
                break;
        }
    }

    private async buildNewsCardView(news: NewsCard): Promise<NewsCardView> {
        const guestAvatar = new DynamicResourcesPackage();
        guestAvatar.add(news.guest.avatar);
        await guestAvatar.load();
        switch (news.constructor) {
            case VisitNewsCard:
                this.accrueResultTimeOffset = 1.8;
                return new VisitNewsCardView(
                    this.canvasScaler,
                    this.newsResourcesPackage,
                    this.commonResources,
                    this.userProfileResources,
                    guestAvatar.resource(news.guest.avatar),
                    news as VisitNewsCard,
                    this.playerModel,
                    this.newsAnimationTargetProvider
                );
            case RaidNewsCard:
                this.accrueResultTimeOffset = 1.0;
                if (this.playerModel.shields.value > 0) {
                    return new RaidNewsCardView(
                        this.canvasScaler,
                        this.newsResourcesPackage,
                        this.userProfileResources,
                        guestAvatar.resource(news.guest.avatar),
                        news.guest.firstName.toUpperCase(),
                        this.playerModel,
                        news as RaidNewsCard
                    );
                } else {
                    return new RaidNewsCardViewWithoutShields(
                        this.canvasScaler,
                        this.newsResourcesPackage,
                        this.userProfileResources,
                        guestAvatar.resource(news.guest.avatar),
                        news.guest.firstName.toUpperCase(),
                        this.playerModel,
                        news as RaidNewsCard
                    );
                }
            case StealNewsCard:
                this.accrueResultTimeOffset = 2.0;
                return new StealNewsCardView(
                    this.canvasScaler,
                    this.newsResourcesPackage,
                    this.userProfileResources,
                    guestAvatar.resource(news.guest.avatar),
                    news as StealNewsCard,
                    this.playerModel,
                );
        }
    }

    private async show(news: NewsCard): Promise<void> {
        const newsCardView = await this.asyncOperationNotification.autoShowHide(this.buildNewsCardView(news));
        const waitForComplete = new PromiseCompletionSource<void>();
        const topLeft = this.parent.toLocal({ x: 0, y: 0 });
        const timeline = new gsap.core.Timeline();
        timeline.fromTo(
            newsCardView.position,
            { y: topLeft.y - newsCardView.height },
            {
                y: topLeft.y,
                duration: 0.8,
                ease: "expo.in",
                onStartParams: [this],
                onStart: function (self) { self.addChild(newsCardView); }
            }
        );
        timeline.to(
            newsCardView.position,
            {
                y: -newsCardView.height,
                duration: 0.8,
                ease: "expo.out",
            },
            ">1.5"
        );
        timeline.call(
            function () {
                newsCardView.accrueResult(waitForComplete);
            },
            [],
            `-=${this.accrueResultTimeOffset}`
        );
        await timeline.play();
        await waitForComplete.result;
        this.removeChild(newsCardView);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.newsModel.propertyChanged.remove(this.onNewsModelChange, this);
        super.destroy(options);
    }
}
